/**
 * DecisionLogPage — J5 B4
 *
 * Enhanced Decision Log with filter bar:
 *  - Impact (Root/Trunk/Branch/Leaf)
 *  - Domain
 *  - Method
 *  - Date range (from/to)
 *
 * Columns: title, impact, method, decider, date, linked work item, outcome.
 *
 * PRD §12.3.8 task 4.
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GitBranch, Plus, X, Filter } from 'lucide-react';
import { t } from '@/lib/i18n';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { DateInput } from '@/components/ui/DateInput';
import { useRorosDecisions, useRorosDecision, useRorosDecisionMethods } from '@/hooks/roros/decisions';
import { useRorosDomains } from '@/hooks/roros/domains';
import { useFormSheetUrlRouting } from '@/hooks/useFormSheetUrlRouting';
import { formatDate } from '@/lib/localeFormatters';
import { DecisionFormDialog } from './DecisionFormDialog';
import type { RorosDecisionNoteDTO } from '@/types/roros/decisions';
import type { RorosDomainDTO } from '@/types/roros/domains';
import type { DecisionMethodProfile } from '@/types/roros/decisions';

// ─── Impact badge variant ─────────────────────────────────────────────────────

function impactBadgeVariant(impact: string): 'error' | 'warning' | 'secondary' | 'default' {
  switch (impact) {
    case 'root': return 'error';
    case 'trunk': return 'warning';
    case 'branch': return 'secondary';
    default: return 'default';
  }
}

// ─── Status badge variant ─────────────────────────────────────────────────────

function statusBadgeVariant(status: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'approved': return 'success';
    case 'pending_approval': return 'warning';
    case 'rejected': return 'error';
    default: return 'default';
  }
}

// ─── Heuristic to detect Founder-mode auto-approval ───────────────────────────

function isAutoApproved(d: RorosDecisionNoteDTO): boolean {
  if (d.status !== 'approved' || !d.approvedAt || !d.createdAt) return false;
  const diff = Math.abs(new Date(d.approvedAt).getTime() - new Date(d.createdAt).getTime());
  return diff < 10_000;
}

function formatMethodLabel(slug: string): string {
  return slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Impact filter options ─────────────────────────────────────────────────────
// IMPORTANT: Radix Select.Item throws on `value=""`. Use the sentinel
// `ALL_SENTINEL` for the "all impacts/domains/methods" rows. The filter
// state still uses '' to mean "no filter" — the Select layer translates.

const ALL_SENTINEL = '__all__';

const IMPACT_OPTIONS = [
  { value: ALL_SENTINEL, label: t('All impacts') },
  { value: 'root', label: t('Root') },
  { value: 'trunk', label: t('Trunk') },
  { value: 'branch', label: t('Branch') },
  { value: 'leaf', label: t('Leaf') },
];

// ─── Filter bar ───────────────────────────────────────────────────────────────

interface FilterState {
  impact: string;
  domainId: string;
  methodSlug: string;
  dateFrom: string;
  dateTo: string;
}

const EMPTY_FILTERS: FilterState = {
  impact: '',
  domainId: '',
  methodSlug: '',
  dateFrom: '',
  dateTo: '',
};

// Translate '' filter value (state) to ALL_SENTINEL (Select value), and back.
const toSelectValue = (v: string) => (v === '' ? ALL_SENTINEL : v);
const fromSelectValue = (v: string) => (v === ALL_SENTINEL ? '' : v);

interface FilterBarProps {
  filters: FilterState;
  onChange: (f: FilterState) => void;
  domains: RorosDomainDTO[];
  methods: DecisionMethodProfile[];
}

function FilterBar({ filters, onChange, domains, methods }: FilterBarProps) {
  const hasActive =
    filters.impact || filters.domainId || filters.methodSlug || filters.dateFrom || filters.dateTo;

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-3 mb-4">
      <div className="flex flex-wrap items-end gap-3">
        {/* Impact filter */}
        <div className="flex flex-col gap-1 min-w-[120px]">
          <label className="text-xs font-medium text-muted-foreground">{t('Impact')}</label>
          <Select
            value={toSelectValue(filters.impact)}
            onValueChange={(v) => onChange({ ...filters, impact: fromSelectValue(v) })}
            options={IMPACT_OPTIONS}
            data-testid="filter-impact"
          />
        </div>

        {/* Domain filter */}
        <div className="flex flex-col gap-1 min-w-[150px]">
          <label className="text-xs font-medium text-muted-foreground">{t('Domain')}</label>
          <Select
            value={toSelectValue(filters.domainId)}
            onValueChange={(v) => onChange({ ...filters, domainId: fromSelectValue(v) })}
            options={[
              { value: ALL_SENTINEL, label: t('All domains') },
              ...domains.map((d) => ({ value: d.id, label: d.name })),
            ]}
            data-testid="filter-domain"
          />
        </div>

        {/* Method filter */}
        <div className="flex flex-col gap-1 min-w-[150px]">
          <label className="text-xs font-medium text-muted-foreground">{t('Method')}</label>
          <Select
            value={toSelectValue(filters.methodSlug)}
            onValueChange={(v) => onChange({ ...filters, methodSlug: fromSelectValue(v) })}
            options={[
              { value: ALL_SENTINEL, label: t('All methods') },
              ...methods.map((m) => ({ value: m.slug, label: m.name })),
            ]}
            data-testid="filter-method"
          />
        </div>

        {/* Date from */}
        <div className="flex flex-col gap-1 min-w-[130px]">
          <label className="text-xs font-medium text-muted-foreground">{t('From')}</label>
          <DateInput
            value={filters.dateFrom}
            onChange={(v) => onChange({ ...filters, dateFrom: v })}
            placeholder={t('Start date')}
          />
        </div>

        {/* Date to */}
        <div className="flex flex-col gap-1 min-w-[130px]">
          <label className="text-xs font-medium text-muted-foreground">{t('To')}</label>
          <DateInput
            value={filters.dateTo}
            onChange={(v) => onChange({ ...filters, dateTo: v })}
            placeholder={t('End date')}
          />
        </div>

        {/* Clear filters */}
        {hasActive && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onChange(EMPTY_FILTERS)}
            className="flex-shrink-0"
            data-testid="clear-filters-btn"
          >
            <X className="h-4 w-4 mr-1" aria-hidden />
            {t('Clear filters')}
          </Button>
        )}
      </div>

      {hasActive && (
        <div className="mt-2 flex items-center gap-1.5">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          <span className="text-xs text-muted-foreground">{t('Filters active')}</span>
        </div>
      )}
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export function DecisionLogPage() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  // Phase 4 fix-4: hold decision ID (not the snapshot) so the dialog re-renders
  // with fresh data after mutations (Run Decision Moderator, Consult Advisory
  // Board) invalidate the query — moderator output appears inline immediately
  // instead of requiring a close + reopen.
  const [editingDecisionId, setEditingDecisionId] = useState<string | null>(null);
  // F-Ref-24 fix: separate boolean to control edit-sheet open state.
  // Previously isOpen={!!editingDecision} caused urlId to become undefined
  // simultaneously with isOpen=false, so FormSheet's URL cleanup bailed early
  // (condition: !urlId). Now isOpen=isEditFormOpen, and editingDecisionId is
  // NOT cleared immediately on close — it stays set so urlId remains valid
  // while FormSheet runs its ?edit-decision param removal. Pattern matches GoalsPage.
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);

  // CLEAN-J7-W6-A4 (dogfood-fix-wave-2 F5): deep-link support via ?openId=<decisionId>
  // Fired by Exploration Zones pages when navigating to a specific Decision Note.
  const [searchParams, setSearchParams] = useSearchParams();

  const decisionsQuery = useRorosDecisions();
  const domainsQuery = useRorosDomains();
  const methodsQuery = useRorosDecisionMethods();

  const domainMap = useMemo(() => {
    const map = new Map<string, string>();
    (domainsQuery.data ?? []).forEach((d: RorosDomainDTO) => map.set(d.id, d.name));
    return map;
  }, [domainsQuery.data]);

  const domains = useMemo(
    () => (domainsQuery.data ?? []).filter((d: RorosDomainDTO) => d.isActive),
    [domainsQuery.data],
  );

  const methods: DecisionMethodProfile[] = useMemo(
    () => (methodsQuery.data ?? []),
    [methodsQuery.data],
  );

  // Client-side filtering
  const allDecisions: RorosDecisionNoteDTO[] = useMemo(() =>
    Array.isArray(decisionsQuery.data)
      ? decisionsQuery.data
      : ((decisionsQuery.data as any)?.items ?? []),
  [decisionsQuery.data]);

  // F5/F7 dogfood-fix-wave-1: use single-decision query when dialog is open so
  // the dialog always gets fresh data after the moderator or advisory consultation
  // mutates the record. The single-decision query is invalidated by those mutations
  // (useRorosDecisionRunModerator + useRorosDecisionAdvisoryConsult both call
  // qc.invalidateQueries({ queryKey: rorosKeys.decisions.detail(id) })), so the
  // dialog prop updates and the useEffect re-hydrates form.methodSlug correctly.
  const freshDecisionQuery = useRorosDecision(editingDecisionId ?? '');
  const editingDecision = useMemo(
    () => {
      if (!editingDecisionId) return null;
      // Prefer the single-decision query result (freshest); fall back to list snapshot
      const fresh = freshDecisionQuery.data as RorosDecisionNoteDTO | undefined;
      if (fresh) return fresh;
      return allDecisions.find((d) => d.id === editingDecisionId) ?? null;
    },
    [editingDecisionId, allDecisions, freshDecisionQuery.data],
  );

  // ─── W4c URL routing — ?edit-decision=<id> ─────────────────────────────────
  // Deep-link: navigating to /roros/decisions?edit-decision=<id> opens the
  // edit sheet directly. Browser Back closes (dirty-guarded via FormSheet).
  // getId must be stable (useCallback) per useFormSheetUrlRouting contract.
  const getDecisionId = useCallback((d: RorosDecisionNoteDTO) => d.id, []);
  const { deepLinkTarget, consumeDeepLink } = useFormSheetUrlRouting({
    urlKey: 'edit-decision',
    items: allDecisions,
    getId: getDecisionId,
  });

  useEffect(() => {
    if (!deepLinkTarget) return;
    setEditingDecisionId(deepLinkTarget.id);
    setIsEditFormOpen(true);
    consumeDeepLink();
  }, [deepLinkTarget, consumeDeepLink]);

  // CLEAN-J7-W6-A4 (F5): when ?openId=<id> is present and the list has loaded,
  // open the edit dialog for that decision and clear the query param.
  const openId = searchParams.get('openId');
  useEffect(() => {
    if (!openId || allDecisions.length === 0) return;
    const found = allDecisions.find((d) => d.id === openId);
    if (found) {
      setEditingDecisionId(found.id);
      setIsEditFormOpen(true);
      // Remove the param after opening so the URL stays clean on close
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('openId');
        return next;
      }, { replace: true });
    }
  }, [openId, allDecisions]);

  const filteredDecisions = useMemo(() => {
    const all: RorosDecisionNoteDTO[] = allDecisions;

    return all.filter((d) => {
      if (filters.impact && d.impactClassification !== filters.impact) return false;
      if (filters.domainId && d.domainId !== filters.domainId) return false;
      if (filters.methodSlug && d.methodUsed !== filters.methodSlug) return false;
      if (filters.dateFrom && d.createdAt < filters.dateFrom) return false;
      if (filters.dateTo && d.createdAt > filters.dateTo + 'T23:59:59') return false;
      return true;
    });
  }, [decisionsQuery.data, filters]);

  return (
    <RorosPage
      title={t('Decision Log')}
      icon={<GitBranch className="h-5 w-5" aria-hidden />}
      actions={
        <Button size="sm" onClick={() => setIsFormOpen(true)}>
          <Plus className="h-4 w-4 mr-1" aria-hidden />
          {t('New Decision Note')}
        </Button>
      }
    >
      {/* Filter bar */}
      <FilterBar
        filters={filters}
        onChange={setFilters}
        domains={domains}
        methods={methods}
      />

      <QueryPage
        query={decisionsQuery}
        emptyMessage={t('No Decision Notes yet. Create the first one to start documenting your decisions.')}
        emptyIcon={<GitBranch className="h-8 w-8" />}
        emptyAction={
          <Button size="sm" onClick={() => setIsFormOpen(true)} data-testid="empty-state-create-decision">
            <Plus className="h-4 w-4 mr-1" />
            {t('Create your first Decision Note')}
          </Button>
        }
      >
        {() => (
          <div className="overflow-x-auto rounded-lg border border-border">
            {filteredDecisions.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-10 text-center">
                <GitBranch className="h-8 w-8 text-muted-foreground" aria-hidden />
                <p className="text-sm text-muted-foreground">
                  {t('No decisions match the active filters.')}
                </p>
                <Button size="sm" variant="ghost" onClick={() => setFilters(EMPTY_FILTERS)}>
                  {t('Clear filters')}
                </Button>
              </div>
            ) : (
              <table
                className="w-full min-w-[720px] text-sm"
                aria-label={t('Decision Log')}
              >
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5">
                      {t('Title')}
                    </th>
                    <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-24">
                      {t('Impact')}
                    </th>
                    <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-32">
                      {t('Domain')}
                    </th>
                    <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-36">
                      {t('Method')}
                    </th>
                    <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-28">
                      {t('Status')}
                    </th>
                    <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-28">
                      {t('Date')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDecisions.map((d: RorosDecisionNoteDTO) => (
                    <tr
                      key={d.id}
                      onClick={() => { setEditingDecisionId(d.id); setIsEditFormOpen(true); }}
                      className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors cursor-pointer"
                      data-testid={`decision-row-${d.id}`}
                    >
                      <td className="px-4 py-3 font-medium text-foreground max-w-xs">
                        <div className="truncate" title={d.title}>{d.title}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={impactBadgeVariant(d.impactClassification)} className="capitalize">
                          {d.impactClassification || '—'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {d.domainId ? (domainMap.get(d.domainId) ?? t('—')) : t('—')}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {d.methodUsed ? formatMethodLabel(d.methodUsed) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-0.5">
                          <Badge variant={statusBadgeVariant(d.status)}>
                            {d.status.replace(/_/g, ' ')}
                          </Badge>
                          {isAutoApproved(d) && (
                            <span className="text-xs text-muted-foreground" title={t('Auto-approved in Founder mode')}>
                              ⚡ {t('Founder')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">
                        {d.createdAt ? formatDate(new Date(d.createdAt)) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </QueryPage>

      {/* Create dialog. After a successful create we re-open the SAME just-
          created decision in the edit dialog (line 417 below) so the user
          can run the Decision Moderator on it without hunting for the row
          in the list. Sven dogfood 2026-05-03: pre-fix the create dialog
          closed and the moderator button was hidden until the user clicked
          back into the row. */}
      <DecisionFormDialog
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onCreated={(newId) => {
          decisionsQuery.refetch();
          setIsFormOpen(false);
          if (newId) {
            setEditingDecisionId(newId);
            setIsEditFormOpen(true);
          }
        }}
      />

      {/* Edit dialog — opens when a row is clicked. Decision is derived from
          the live query so moderator output appears inline after the run.
          urlKey="edit-decision" syncs ?edit-decision=<id> in the URL for
          deep-linking (AC-W4c-02).
          F-Ref-24: isOpen uses separate isEditFormOpen boolean (not !!editingDecision)
          so urlId remains valid (non-null) when isOpen=false, allowing FormSheet's
          URL sync effect to properly remove ?edit-decision from the URL. */}
      <DecisionFormDialog
        isOpen={isEditFormOpen}
        onClose={() => setIsEditFormOpen(false)}
        onCreated={() => { setIsEditFormOpen(false); decisionsQuery.refetch(); }}
        decision={editingDecision}
        urlKey="edit-decision"
        urlId={editingDecision?.id}
      />
    </RorosPage>
  );
}
